import * as React from 'react';
import {connect} from 'react-redux';

import _ from 'underscore';

import {Users} from 'nutshell-core/users';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {CheckboxButton} from 'shells/checkbox/checkbox-button';
import {CheckboxGroup} from 'shells/checkbox/checkbox-group';
import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FormField} from 'shells/form/form-field';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {SelectInlineCreatable} from 'shells/select-inline/select-inline-creatable';
import {ModalBody} from 'shells/typography/modal-body';
import {Title} from 'shells/typography/title';
import {PlanUpgradeLink} from 'shells/upgrade/plan-upgrade-link';

import type {NutshellMarketingStats} from 'nutshell-graphql-types';

import {useGetUsageStats} from '../../graphql/hooks/use-get-usage-stats';

import {getHasExceededContactLimit, getRemainingSends} from './helpers';

import styles from './send-test-modal.module.css';

type EmailAddress = {
    id: string;
    name: string;
    userName?: string;
};

type OwnProps = {
    isOpen: boolean;
    onClose: () => void;
    onSend: (arg1: string[]) => void;
    isSending: boolean;
    testEmailErrorMessage: string | null | undefined;
    onHideTestEmailErrorMessage: () => void;
    isABTesting?: boolean;
};

type StateProps = {
    userOptions: EmailAddress[];
};

type Props = OwnProps & StateProps;

type ComponentProps = Props & {
    usageStats: NutshellMarketingStats;
};

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        // @ts-expect-error
        userOptions: Users.getUserEmailOptions(state),
    };
}

function SendTestModalComponent(props: ComponentProps) {
    const currentUserEmailAddress = props.userOptions.filter(
        (userEmailAddress) => userEmailAddress.id === NutClientConfig.loggedInUserEmail
    );

    // All user email addresses besides the current logged in user
    const otherUserEmailAddresses = props.userOptions.filter(
        (userEmailAddress) => userEmailAddress.id !== NutClientConfig.loggedInUserEmail
    );

    // All selected email addresses, INCLUDING custom hand-typed email addresses that
    // do not relate to a user (i.e. litmustest1234@litmus.com)
    const [selectedEmailAddresses, setSelectedEmailAddresses] =
        React.useState<EmailAddress[]>(currentUserEmailAddress);

    // The (up to 8) user email addresses we'll display in a checkboxes below
    const [checkboxAvailableUserEmailAddresses] = React.useState<EmailAddress[]>( // Takes the current user email address and 7 other random user emails
        // and gets us up to 8 emails to display
        currentUserEmailAddress.concat(_.sample(otherUserEmailAddresses, 7))
    );

    // We'll bucket email addresses by id to make our CheckboxGroup onChange function
    // slightly easier to deal with
    const [userEmailAddressesById, setUserEmailAddressesById] = React.useState<{
        [key: string]: EmailAddress;
    }>({});

    // Any time user options change, set our "userEmailAddressesById" state with those
    // user options
    //
    // e.g. {[ccain@nutshell.com]: {id: 'ccain@nutshell.com', name: 'Chris Cain'}}
    React.useEffect(() => {
        setUserEmailAddressesById(
            props.userOptions.reduce<Record<string, any>>((acc, emailAddress) => {
                return {...acc, [emailAddress.id]: emailAddress};
            }, {})
        );
    }, [props.userOptions]);

    const numberOfMarketableContacts = props.usageStats.usageStats.numberOfMarketableContacts;
    const maxNumberOfMarketableContacts = props.usageStats.gate.maxNumberOfMarketableContacts;
    const marketingContactsOverageAllowance =
        props.usageStats.gate.marketingContactsOverageAllowance;
    const hasUnlimitedMarketingContacts = props.usageStats.gate.hasUnlimitedMarketingContacts;
    const maxNumberOfEmailsPerMonth = props.usageStats.gate.maxNumberOfEmailsPerMonth;
    const numberOfEmailsSentThisMonth = props.usageStats.usageStats.numberOfEmailsSentThisMonth;

    const remainingSends = getRemainingSends({
        maxNumberOfEmailsPerMonth,
        numberOfEmailsSentThisMonth,
    });

    const exceededContactLimit = getHasExceededContactLimit({
        hasUnlimitedMarketingContacts,
        marketingContactsOverageAllowance,
        numberOfMarketableContacts,
        maxNumberOfMarketableContacts,
    });

    const hasSendingError =
        exceededContactLimit ||
        (typeof remainingSends === 'number' && selectedEmailAddresses.length > remainingSends);

    let errorMessage = '';

    // Is the user already over their account limit? (Sent 100/100 emails)
    const alreadyOverLimit =
        exceededContactLimit || (typeof remainingSends === 'number' && remainingSends <= 0);

    if (alreadyOverLimit) {
        errorMessage =
            'Hang tight! You’ll need to upgrade or wait until next month to resume sending emails.';
    } else if (
        typeof remainingSends === 'number' &&
        selectedEmailAddresses.length > remainingSends
    ) {
        // Also check if the sends are going to put the user over their limit
        // (already sent 99/100, want to send 3 more)
        // You can only send {remaingSends} more [free] marketing message[s]
        // this month.
        errorMessage = `You can only send ${remainingSends} more ${
            props.usageStats.gate.maxNumberOfMarketableContacts === null ? 'free' : ''
        } marketing ${remainingSends === 1 ? 'message' : 'messages'} this month.`;
    }

    return (
        <Modal
            headerText={props.isABTesting ? 'Send test emails' : 'Send a test email'}
            isOpen={props.isOpen}
            onClose={props.onClose}
            preventBodyScroll={true}
            maxHeight={700}
        >
            {hasSendingError ? (
                <div className='mb-16'>
                    <FlashBanner type={exceededContactLimit ? 'warning' : 'info'} hasBorder={true}>
                        <div className='full-width flex justify-sb'>
                            <span>{errorMessage}</span>
                            <div className={styles['flash-banner-cta-container']}>
                                <PlanUpgradeLink />
                            </div>
                        </div>
                    </FlashBanner>
                </div>
            ) : undefined}
            {props.isABTesting ? (
                <div className='mb-16'>
                    <FlashBanner leftAlignContent={true} type='info'>
                        We’ll send both ‘A’ and ‘B’ versions as two separate messages
                    </FlashBanner>
                </div>
            ) : undefined}
            {props.testEmailErrorMessage ? (
                <div className='mb-16'>
                    <FlashBanner
                        type={'error'}
                        hasBorder={true}
                        onHidden={props.onHideTestEmailErrorMessage}
                    >
                        <div className='full-width flex justify-sb'>
                            <span>{props.testEmailErrorMessage}</span>
                        </div>
                    </FlashBanner>
                </div>
            ) : undefined}
            <div className={styles.container}>
                <DisabledSection disabled={alreadyOverLimit}>
                    <div className={styles['to-addresses-container']}>
                        <ModalBody>
                            Start typing or choose from the list to add recipients
                        </ModalBody>
                        <div className={styles['email-select-container']}>
                            <SelectInlineCreatable
                                noOptionsMessage={() => 'Type an email address to send to'}
                                tokenizedStyle='light'
                                separatorKeys={[',', ' ', ';', 'Spacebar']}
                                options={props.userOptions}
                                value={selectedEmailAddresses}
                                onChange={setSelectedEmailAddresses}
                                inlineLabel='To'
                                autoFocus={false}
                                // Check that the new option is somestring@somestring.somestring
                                isValidNewOption={(newOption) => /\S+@\S+\.\S+/.test(newOption)}
                                // @ts-expect-error
                                formatCreateLabel={(input) => input}
                                filterOption={() => true}
                                multi={true}
                                styles={{
                                    // Checkboxes have a z-index of 1
                                    // @ts-expect-error
                                    menu: (base) => ({...base, zIndex: 2}),
                                    // @ts-expect-error
                                    valueContainer: (base) => ({
                                        ...base,
                                        maxHeight: '63px',
                                        overflowY: 'auto',
                                    }),
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles['checkbox-container']}>
                        <FormField label='Suggested' innerId='to addresses'>
                            <CheckboxGroup
                                name='email addresses'
                                selectedValues={selectedEmailAddresses.map((email) => email.id)}
                                onChange={(newSelectedEmailAddresses: string[]) => {
                                    const newEmailAddresses = newSelectedEmailAddresses.map(
                                        (emailAddressId) => {
                                            if (userEmailAddressesById[emailAddressId]) {
                                                return userEmailAddressesById[emailAddressId];
                                            }

                                            return selectedEmailAddresses.find((emailAddress) => {
                                                return emailAddress.id === emailAddressId;
                                            });
                                        }
                                    );

                                    // Filtering out any undefined values from newSelectedEmailAddresses
                                    // because find can return null
                                    const nonNullEmailAddresses = newEmailAddresses.filter(
                                        (emailAddress) => emailAddress
                                    );

                                    // @ts-expect-error
                                    setSelectedEmailAddresses(nonNullEmailAddresses);
                                }}
                            >
                                <div className={styles['checkbox-button-containers']}>
                                    {checkboxAvailableUserEmailAddresses.map((emailAddress) => {
                                        return (
                                            <CheckboxButton
                                                value={emailAddress.id}
                                                key={emailAddress.id}
                                            >
                                                <Title>{emailAddress.userName}</Title>
                                                <span>{emailAddress.id}</span>
                                            </CheckboxButton>
                                        );
                                    })}
                                </div>
                            </CheckboxGroup>
                        </FormField>
                    </div>
                </DisabledSection>
            </div>
            <FooterBar
                left={
                    <ButtonMover size='big' direction='left'>
                        <Button variant='text' size='big' onClick={props.onClose}>
                            Cancel
                        </Button>
                    </ButtonMover>
                }
                right={
                    <SaveButton
                        isSaving={props.isSending}
                        errorSaving={Boolean(props.testEmailErrorMessage)}
                        saveCompleteTimeout={2000}
                        onClearSaveComplete={props.onClose}
                        disabled={selectedEmailAddresses.length === 0 || hasSendingError}
                        buttonText={
                            selectedEmailAddresses.length > 0
                                ? `Send test to ${selectedEmailAddresses.length} ${
                                      selectedEmailAddresses.length > 1 ? 'people' : 'person'
                                  }`
                                : 'Send'
                        }
                        savingText='Sending…'
                        successText='Sent!'
                        // @ts-expect-error
                        onClick={() => {
                            props.onSend(
                                selectedEmailAddresses.map((emailAddress) => emailAddress.id)
                            );
                        }}
                    />
                }
            />
        </Modal>
    );
}

function SendTestModalLoadingComponent(props: Props) {
    const {nutshellMarketingStats} = useGetUsageStats();

    if (props.userOptions.length === 0 || !nutshellMarketingStats) {
        // Need loading screen here
        return null;
    }

    return <SendTestModalComponent {...props} usageStats={nutshellMarketingStats} />;
}

// @ts-expect-error
export const SendTestModal = connect<Props, OwnProps, _, _, _, _>(mapStateToProps)(
    SendTestModalLoadingComponent
);
